import { inject, Injectable } from '@angular/core';
import { createStore, withProps } from '@ngneat/elf';
import {
    persistState,
    sessionStorageStrategy,
} from '@ngneat/elf-persist-state';
import {
    ITypeEntity,
    TenantProvider,
    TypeEntityTypes,
} from '@pf/shared-common';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

interface TypeEntityState {
    tenantId: string;
    types: Record<
        string,
        { entities: ITypeEntity[]; currentEntityIds: string[] }
    >;
}

const typeEntityStore = createStore(
    { name: 'typeEntities' },
    withProps<TypeEntityState>({
        tenantId: '',
        types: {},
    })
);

@UntilDestroy()
@Injectable()
export class TypeEntityStore {
    constructor() {
        const persist = persistState(typeEntityStore, {
            storage: sessionStorageStrategy,
        });
        combineLatest([inject(TenantProvider).id$, persist.initialized$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(([tenantId]) => {
                if (typeEntityStore.state.tenantId === tenantId) return;
                typeEntityStore.update(() => ({
                    tenantId: tenantId,
                    types: {},
                }));
            });
    }

    public setTypeEntities(
        typeEntities: ITypeEntity[],
        entityType: TypeEntityTypes
    ): void {
        typeEntityStore.update(state => ({
            ...state,
            types: {
                ...state.types,
                [`${entityType}s`]: {
                    entities: typeEntities,
                    currentEntityIds: [],
                },
            },
        }));
    }

    get(
        entityId: string,
        entityType: TypeEntityTypes
    ): ITypeEntity | undefined {
        const entities =
            typeEntityStore.state.types[`${entityType}s`]?.entities || [];
        return entities.find(entity => entity.id === entityId);
    }

    getByCode(
        code: string,
        entityType: TypeEntityTypes
    ): ITypeEntity | undefined {
        const entities =
            typeEntityStore.state.types[`${entityType}s`]?.entities || [];
        return entities.find(entity => entity.code === code);
    }

    public setCurrentEntities(
        currentEntityIds: string[],
        entityType: TypeEntityTypes
    ): void {
        typeEntityStore.update(state => ({
            ...state,
            types: {
                ...state.types,
                [`${entityType}s`]: {
                    ...state.types[`${entityType}s`],
                    currentEntityIds: currentEntityIds,
                },
            },
        }));
    }

    public getTypeEntities(
        entityType: TypeEntityTypes
    ): Observable<ITypeEntity[]> {
        return this.getEntityTypeState(entityType).pipe(
            map(state => state.entities)
        );
    }

    public getCurrentEntities(
        entityType: TypeEntityTypes
    ): Observable<ITypeEntity[]> {
        return this.getEntityTypeState(entityType).pipe(
            map(state => {
                const currentEntities = state.currentEntityIds;
                return state.entities.filter(entity =>
                    currentEntities.includes(entity.id)
                );
            })
        );
    }

    private getEntityTypeState(entityType: TypeEntityTypes) {
        return typeEntityStore.pipe(
            map(
                state =>
                    state.types[`${entityType}s`] || {
                        entities: [],
                        currentEntityIds: [],
                    }
            )
        );
    }
}
